// "What this agent can access" — a digestible, categorized modal.
//
// Replaces the inline chip wall: the same business data domains, but grouped
// into source cards (Application database / Sage ERP / Knowledge Banks / write)
// with each domain's plain-language description, so a non-technical reader gets
// a clean "it can read Orders, Customers, Production plans…" at a glance. The
// table-level / developer detail lives in the Inspect modal, not here.

import { Modal } from '../common/Modal';
import type { AccessGroup, ConsoleProfile } from '../../services/agentConsole';
import styles from './ProfileAccessModal.module.css';

const KIND_LABEL: Record<string, string> = {
  data: 'reads',
  knowledge: 'searches',
  write: 'writes',
};

function GroupCard({ group }: { group: AccessGroup }) {
  const isWrite = group.kind === 'write';
  return (
    <section className={`${styles.card} ${isWrite ? styles.writeCard : ''}`}>
      <div className={styles.cardHead}>
        <h3 className={styles.cardTitle}>
          {isWrite ? '✍ ' : ''}
          {group.label}
        </h3>
        <span className={`${styles.kindTag} ${isWrite ? styles.kindWrite : ''}`}>
          {KIND_LABEL[group.kind] ?? group.kind}
        </span>
      </div>
      <p className={styles.cardScope}>{group.scope_note}</p>
      <ul className={styles.domainList}>
        {group.domains.map((d) => (
          <li key={d.id} className={styles.domain}>
            <span className={styles.domainLabel}>{d.label}</span>
            <span className={styles.domainDesc}>{d.description}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function ProfileAccessModal({
  open,
  onClose,
  profile,
}: {
  open: boolean;
  onClose: () => void;
  profile: ConsoleProfile | null;
}) {
  const groups = profile?.access_groups ?? [];
  return (
    <Modal
      isOpen={open && !!profile}
      onClose={onClose}
      title={profile ? `What the ${profile.name} can access` : 'Access'}
      size="lg"
    >
      <p className={styles.intro}>
        The data this agent configuration can reach, in plain language. Read
        access is structural — read-only at the credential level. For the exact
        tables behind each area, open <strong>Inspect</strong>.
      </p>
      <div className={styles.grid}>
        {groups.map((g) => (
          <GroupCard key={g.id} group={g} />
        ))}
      </div>
    </Modal>
  );
}
